import { makeAutoObservable, runInAction } from 'mobx';

import { leaderboardService } from '@/services/leaderboard';
import type { LeaderboardEntry } from '@/types/leaderboard';

class LeaderboardStore {
  entries: LeaderboardEntry[] = [];
  myRank: number | null = null;

  refreshing = false;
  refreshError: string | null = null;

  loaded = false;

  // Tracks the in-flight refresh so concurrent callers await the same load
  // instead of firing duplicate fetches. Internal — readers should observe
  // `refreshing` instead.
  inflightLoad: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, { inflightLoad: false }, { autoBind: true });
  }

  /** True before the first successful refresh — drives the initial spinner. */
  get isInitialLoading(): boolean {
    return this.refreshing && !this.loaded;
  }

  get topThree(): LeaderboardEntry[] {
    return this.entries.slice(0, 3);
  }

  get rest(): LeaderboardEntry[] {
    return this.entries.slice(3);
  }

  get inTopTwenty(): boolean {
    return this.myRank !== null && this.myRank <= 20;
  }

  get hasEarnings(): boolean {
    return this.myRank !== null;
  }

  /**
   * Returns true when the given rank is the signed-in user's rank. Backend
   * deliberately omits `user_id` from leaderboard entries; rank is the
   * unambiguous match key for the current board (no two users share rank N).
   */
  isMe(rank: number): boolean {
    return this.myRank !== null && this.myRank === rank;
  }

  /**
   * Idempotent loader for tab mount effects. Skips when a refresh is already
   * in flight or when data has already arrived. Pull-to-refresh should call
   * `refresh()` directly to force a re-fetch.
   */
  ensureLoaded(): Promise<void> {
    if (this.inflightLoad) return this.inflightLoad;
    if (this.loaded) return Promise.resolve();
    return this.refresh().catch(() => {
      // Error captured on store.refreshError; callers don't need to handle.
    });
  }

  /**
   * Pulls the top-20 board and the user's rank in parallel. Independent
   * failure modes: a `me()` rejection isolates to `myRank=null` so the board
   * still renders without a self-highlight. A `top()` rejection becomes the
   * screen-level error state.
   */
  refresh(): Promise<void> {
    if (this.inflightLoad) return this.inflightLoad;
    const promise = this.runRefresh();
    this.inflightLoad = promise;
    return promise;
  }

  private async runRefresh(): Promise<void> {
    runInAction(() => {
      this.refreshing = true;
      this.refreshError = null;
    });
    try {
      // `me()` is wrapped so its failure doesn't reject the outer Promise.all.
      const [entries, meResult] = await Promise.all([
        leaderboardService.top(),
        leaderboardService.me().then(
          (rank) => ({ ok: true, rank }) as const,
          (err: unknown) => ({ ok: false, err }) as const,
        ),
      ]);
      runInAction(() => {
        this.entries = entries;
        this.myRank = meResult.ok ? meResult.rank : null;
        this.loaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.refreshError =
          err instanceof Error ? err.message : 'Failed to load leaderboard.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.refreshing = false;
      });
      this.inflightLoad = null;
    }
  }

  reset(): void {
    runInAction(() => {
      this.entries = [];
      this.myRank = null;
      this.refreshing = false;
      this.refreshError = null;
      this.loaded = false;
    });
  }
}

export const leaderboardStore = new LeaderboardStore();
// `LeaderboardStore` class is exported for tests so each test gets a fresh
// instance. Production code should use the `leaderboardStore` singleton.
export { LeaderboardStore };
